import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { leagueData } from '../types/types';
import axiosInstance from '../axiosConfig';
import { useGlobalStore } from '../store/globalStore';
import { Link, useNavigate } from 'react-router-dom';
import { useClubStore } from '../store/clubStore';
import { useTournamentStore } from '../store/tournamentStore';
import { formatDate } from '../utils/utils';
import { useAuthStore } from '../store/authStore';
import NotFind from '../components/customLayout/notfind';
import SyncPlayerBtn from '../components/customLayout/SyncPlayerBtn';
import tournamentsSearchState from '../assets/images/tournaments-item-search.svg';
import { FileText } from 'lucide-react';

const LeaguesPage: React.FC = () => {
  const isLoading = useGlobalStore((state) => state.isLoading);
  const setLoading = useGlobalStore((state) => state.setLoading);
  const myClub = useClubStore((state) => state.myClub);
  const resetTournament = useTournamentStore((state) => state.resetTournament);
  const navigate = useNavigate();
  const userRole = useAuthStore((state) => state.role);
  const [data, setData] = useState<Array<leagueData>>([]);

  const generateDocFileUrl = (docFile: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return `${import.meta.env.VITE_APP_BASE_URL}${docFile}?time=${new Date().getTime()}`;
  };

  const tableHeader = [
    { key: 'team', text: '🏌️Mannschaft' },
    { key: 'appointment', text: '📅 Termin' },
    { key: 'report', text: '📢 Meldung' },
  ];

  const getList = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('league/list');
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error logging in:', error);
    }
  };

  const isPlayingAsSecondTeam = (
    tournaments: leagueData['classes'][number]['tournaments'],
  ): boolean => {
    if (!myClub?.id) return false;
    const myClubTournaments = tournaments.filter(
      (t) => t.club_id === myClub.id,
    );
    const hasFirstTeam = myClubTournaments.some((t) => !t.zweites_team);
    const hasSecondTeam = myClubTournaments.some((t) =>
      Boolean(t.zweites_team),
    );
    return hasFirstTeam && hasSecondTeam;
  };

  const formatFinalRegistrationDate = (dateString: string): string => {
    if (!dateString) return '';
    const [datePart, timePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-');
    return `${day}.${month}.${year.slice(2)} ${timePart}`;
  };

  const generateTournament = async (
    ligaId: number,
    classId: number,
    teamId: number,
    clubId: number,
    date: string,
    secondTeam: boolean,
    registerId: number,
    finalRegistrationDate: string,
  ) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('league/generateTournament', {
        id: ligaId,
        class_id: classId,
        team_id: teamId,
        club_id: clubId,
        second_team: secondTeam,
        register_id: registerId,
        date,
      });
      navigate(
        `/tournaments/${response.data.tournament_id}?deadline=${finalRegistrationDate}`,
      );
      setLoading(false);
    } catch (error) {
      console.error('Error logging in:', error);
    }
  };

  useEffect(() => {
    getList();
  }, []);

  return (
    <div className={`grid gap-10 px-5`}>
      <div className="border bg-white p-4 grid gap-2 border-neutral-100 hover:shadow hover:border-neutral-300 transition rounded-sm">
        <div className="font-medium text-lg mb-4">Ligen </div>
        {data.length === 0 ? (
          <div className="py-10">
            {isLoading ? (
              <div className="text-center">
                <img
                  className="mx-auto py-10 animate-bounce"
                  alt="tournaments not find "
                  src={tournamentsSearchState}
                />
                <div className="text-2xl">Bitte warten Sie.</div>
                <div>
                  {' '}
                  Wir durchsuchen die Datenbank, um die Liste für Sie
                  anzuzeigen. Bitte warten Sie...
                </div>
              </div>
            ) : (
              <NotFind />
            )}
          </div>
        ) : (
          <>
            {data.map((item, index) => (
              <div key={index}>
                <div className="bg-gradient-to-r from-[#52A445] to-[#105D1F] text-white text-lg font-medium p-2 md:sticky md:top-16 z-20">
                  {item.title}
                </div>
                {item.classes.map((classItem, key) => (
                  <div key={key}>
                    <div className="border bg-green-50 text-lg border-neutral-300 font-medium p-2 md:sticky md:top-[100px]  z-10 shadow-sm">
                      ⛳ {classItem.title}
                    </div>
                    <div className="my-1">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {tableHeader.map((header, key) => (
                                <TableHead
                                  className={`whitespace-nowrap ${header.key === 'team' ? 'w-1/3' : 'w-1/4'}`}
                                  key={key}
                                >
                                  {header.text}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {classItem.tournaments.map((tournament, index) => (
                              <TableRow key={index}>
                                <TableCell className="whitespace-nowrap font-medium">
                                  {tournament.anzeigename}
                                </TableCell>
                                <TableCell>
                                  {tournament.heimspiel &&
                                  tournament.heimspiel ? (
                                    <>{formatDate(tournament.heimspiel)}</>
                                  ) : (
                                    '...'
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div onClick={() => resetTournament()}>
                                    {userRole === 'admin' && (
                                      <Link
                                        className="text-[#52A445] flex items-center gap-0.5 hover:underline"
                                        to={`/league/result-submission/${classItem.lig_id}/${tournament.klassen_id}/${tournament.team_id}/${tournament.anmeldung_id}/${tournament.day}`}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          strokeWidth={3}
                                          stroke="currentColor"
                                          className="size-3"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="m8.25 4.5 7.5 7.5-7.5 7.5"
                                          />
                                        </svg>
                                        Ergebniseingabe
                                      </Link>
                                    )}

                                    <div>
                                      {tournament.doc_file && (
                                        <div className="flex text-[#52A445] hover:underline  items-center text-center">
                                          <FileText
                                            size={20}
                                            aria-label="pdf"
                                          />
                                          <a
                                            target="_blank"
                                            rel="noreferrer"
                                            href={generateDocFileUrl(
                                              tournament.doc_file,
                                            )}
                                          >
                                            Anhang anzeigen
                                          </a>
                                        </div>
                                      )}
                                    </div>

                                    {!tournament.heimspiel &&
                                      tournament.club_id === myClub?.id && (
                                        <div className="text-gray-400">
                                          🗓️ Kein Heimspiel
                                        </div>
                                      )}

                                    {tournament.heimspiel && (
                                      <>
                                        {/* =============== Meldung abgelaufen ===============  */}
                                        {new Date(
                                          tournament.heimspiel,
                                        ).getTime() /
                                          1000 <=
                                          item.meldung_ab +
                                            Math.floor(Date.now() / 1000) && (
                                          <>
                                            {!classItem.only_result && (
                                              <>
                                                {tournament.is_registration_active ? (
                                                  <>
                                                    <div
                                                      onClick={() =>
                                                        generateTournament(
                                                          item.id,
                                                          classItem.id,
                                                          tournament.team_id,
                                                          tournament.club_id,
                                                          tournament.heimspiel,
                                                          false,
                                                          tournament.anmeldung_id,
                                                          formatFinalRegistrationDate(
                                                            tournament.final_registration_date,
                                                          ),
                                                        )
                                                      }
                                                      className="text-[#52A445] flex gap-0.5 items-center hover:underline cursor-pointer"
                                                    >
                                                      <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={3}
                                                        stroke="currentColor"
                                                        className="size-3"
                                                      >
                                                        <path
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                          d="m8.25 4.5 7.5 7.5-7.5 7.5"
                                                        />
                                                      </svg>
                                                      {formatFinalRegistrationDate(
                                                        tournament.final_registration_date,
                                                      )}{' '}
                                                      Uhr
                                                    </div>
                                                    {isPlayingAsSecondTeam(
                                                      classItem.tournaments,
                                                    ) && (
                                                      <>
                                                        <div className="border-t border-dashed my-3"></div>
                                                        <div>
                                                          {tournament.doc_file2 && (
                                                            <div className="flex text-[#52A445] hover:underline  items-center text-center">
                                                              <FileText
                                                                size={20}
                                                                aria-label="pdf"
                                                              />
                                                              <a
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                href={generateDocFileUrl(
                                                                  tournament.doc_file2,
                                                                )}
                                                              >
                                                                Anhang anzeigen
                                                              </a>
                                                            </div>
                                                          )}
                                                        </div>{' '}
                                                        <div
                                                          onClick={() =>
                                                            generateTournament(
                                                              item.id,
                                                              classItem.id,
                                                              tournament.team_id,
                                                              tournament.club_id,
                                                              tournament.heimspiel,
                                                              true,
                                                              tournament.anmeldung_id,
                                                              formatFinalRegistrationDate(
                                                                tournament.final_registration_date,
                                                              ),
                                                            )
                                                          }
                                                          className="text-[#52A445] flex gap-0.5 items-center hover:underline cursor-pointer"
                                                        >
                                                          <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={3}
                                                            stroke="currentColor"
                                                            className="size-3"
                                                          >
                                                            <path
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                              d="m8.25 4.5 7.5 7.5-7.5 7.5"
                                                            />
                                                          </svg>
                                                          {formatFinalRegistrationDate(
                                                            tournament.final_registration_date,
                                                          )}{' '}
                                                          Uhr (2.Team)
                                                        </div>
                                                      </>
                                                    )}
                                                  </>
                                                ) : (
                                                  <div className="cursor-not-allowed text-gray-400">
                                                    🔒{' '}
                                                    {formatFinalRegistrationDate(
                                                      tournament.final_registration_date,
                                                    )}{' '}
                                                    Uhr*
                                                  </div>
                                                )}
                                              </>
                                            )}

                                            {tournament.club_id ===
                                              myClub?.id && (
                                              <>
                                                {tournament.is_result_active ? (
                                                  <div>
                                                    <Link
                                                      className="text-[#52A445] flex items-center gap-0.5 hover:underline"
                                                      to={`/league/result-submission/${classItem.lig_id}/${tournament.klassen_id}/${tournament.team_id}/${tournament.anmeldung_id}/${tournament.day}`}
                                                    >
                                                      <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={3}
                                                        stroke="currentColor"
                                                        className="size-3"
                                                      >
                                                        <path
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                          d="m8.25 4.5 7.5 7.5-7.5 7.5"
                                                        />
                                                      </svg>
                                                      Ergebniseingabe
                                                    </Link>
                                                  </div>
                                                ) : (
                                                  <SyncPlayerBtn
                                                    id={tournament.anmeldung_id}
                                                    name={
                                                      tournament.anzeigename
                                                    }
                                                  />
                                                )}
                                              </>
                                            )}
                                          </>
                                        )}
                                        {/* =============== Meldung abgelaufen ===============  */}
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaguesPage;
